/**
 * Who is signed in.
 *
 * WHY a context rather than a per-page /auth/me call: the session cookie is HttpOnly, so the SPA
 * cannot read it — the only way to know is to ask the API once and remember. This provider also
 * owns the global 401 hook from api.ts, so an expired session anywhere flips the whole app to
 * signed-out in one place instead of every screen guessing.
 */
import { createContext, use, useEffect, useMemo, useState, type ReactNode } from "react";
import { api, setUnauthorizedHandler, type User } from "./api";

export type Session =
  | { status: "loading"; user: null }
  | { status: "anonymous"; user: null }
  | { status: "authenticated"; user: User };

interface SessionContextValue {
  session: Session;
  refresh: () => void;
  signOut: () => Promise<void>;
}

const SessionContext = createContext<SessionContextValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }): ReactNode {
  const [session, setSession] = useState<Session>({ status: "loading", user: null });
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    setUnauthorizedHandler(() => setSession({ status: "anonymous", user: null }));
    return () => setUnauthorizedHandler(null);
  }, []);

  useEffect(() => {
    let live = true;
    api.me().then(
      ({ user }) => { if (live) setSession({ status: "authenticated", user }); },
      // A 401 already set "anonymous" via the hook; anything else (API down) is also not a
      // usable session, and the login screen is the honest place to land.
      () => { if (live) setSession({ status: "anonymous", user: null }); },
    );
    return () => { live = false; };
  }, [nonce]);

  const value = useMemo<SessionContextValue>(() => ({
    session,
    refresh: () => setNonce((n) => n + 1),
    signOut: async () => {
      try { await api.logout(); } finally { setSession({ status: "anonymous", user: null }); }
    },
  }), [session]);

  return <SessionContext value={value}>{children}</SessionContext>;
}

export function useSession(): SessionContextValue {
  const ctx = use(SessionContext);
  if (!ctx) throw new Error("useSession must be used inside <SessionProvider>");
  return ctx;
}

/** Convenience for the many screens that only render when a user exists. */
export function useUser(): User {
  const { session } = useSession();
  if (session.status !== "authenticated") throw new Error("useUser outside an authenticated route");
  return session.user;
}
